(() => {
  if (window.DairyMindDirectPurchase) return;
  const configNode = document.querySelector('[data-direct-purchase-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const marker = '_auto_gateway_for_moocollar';
  const directFlow = 'DIRECT_NO_MOQ';
  const commercialAttributes = ['commercial_model', 'order_quantity', 'herd_size', 'farm_type'];
  let queue = Promise.resolve();
  const positiveQuantity = (value) => {
    const number = Number(value);
    return /^[0-9]+$/.test(String(value)) && Number.isSafeInteger(number) && number > 0 ? number : null;
  };
  const isCollar = (item) => item.handle === 'moocollar-bree' && !item.properties?._commercial_model;
  const isGateway = (item) => item.handle === 'gateway';
  const isAutoGateway = (item) => isGateway(item) && !item.properties?._commercial_model &&
    (item.properties?.[marker] === true || item.properties?.[marker] === 'true');
  const sellingPlanId = (item) => item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null;
  const sortedProperties = (properties = {}) => Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const identity = (item) => JSON.stringify([Number(item.variant_id || item.id), sortedProperties(item.properties), sellingPlanId(item)]);
  const reference = (item) => ({ key: item.key, identity: identity(item) });
  const findLine = (cart, ref) => {
    const key = typeof ref === 'string' ? ref : ref.key;
    const exact = cart.items.find((item) => item.key === key);
    if (exact && (typeof ref === 'string' || identity(exact) === ref.identity)) return exact;
    const matches = typeof ref === 'string' ? [] : cart.items.filter((item) => identity(item) === ref.identity);
    if (matches.length === 1) return matches[0];
    throw new Error('This cart item changed. Please refresh your cart and try again.');
  };
  const prepareItems = (items) => items.map((item) => {
    if (!config.collarVariantIds.includes(Number(item.id)) || item.properties?._commercial_model) return item;
    if (!positiveQuantity(item.quantity)) throw new Error('Please enter a whole number of collars (1 or more).');
    return { ...item, properties: { ...item.properties, _purchase_flow: directFlow } };
  });

  async function request(path, data) {
    const response = await fetch(`${root}${path}`, {
      method: data ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(data ? { 'Content-Type': 'application/json' } : {}) },
      ...(data ? { body: JSON.stringify(data) } : {})
    });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(result.description || result.message || 'Your cart could not be updated. Please try again.');
      error.cartRejected = response.status >= 400 && response.status < 500;
      throw error;
    }
    return result;
  }
  const readCart = () => request('cart.js');
  function enqueue(callback) {
    const task = () => navigator.locks?.request
      ? navigator.locks.request('dairymind-direct-cart', callback) : callback();
    queue = queue.catch(() => {}).then(task);
    return queue;
  }
  async function gatewayVariant() {
    const id = Number(config.gatewayVariantId);
    if (!id || !config.gatewayProductPath) throw new Error('The required Gateway is not configured. Please contact support.');
    const url = new URL(config.gatewayProductPath, window.location.origin);
    const productPath = url.pathname.startsWith(root) ? url.pathname : `${root}${url.pathname.replace(/^\//, '')}`;
    const response = await fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Gateway availability could not be checked. Please retry.');
    const product = await response.json();
    const variant = product.variants?.find((item) => Number(item.id) === id);
    if (!variant?.available) throw new Error('The required Gateway is currently unavailable. Please contact support or retry later.');
    return id;
  }
  async function reconcile() {
    try {
      // Discounts can change line keys, so every repair starts from a fresh cart.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const cart = await readCart();
        const collars = cart.items.filter(isCollar);
        const automatic = cart.items.filter(isAutoGateway);
        const updates = {};
        automatic.forEach((item, index) => {
          const quantity = collars.length && index === 0 ? 1 : 0;
          if (item.quantity !== quantity) updates[item.key] = quantity;
        });
        if (Object.keys(updates).length) {
          await request('cart/update.js', { updates });
          continue;
        }
        if (collars.some((item) => item.quantity > 0) && !cart.items.some((item) => isGateway(item) && item.quantity > 0)) {
          const id = await gatewayVariant();
          await request('cart/add.js', { items: [{ id, quantity: 1, properties: { [marker]: 'true', _purchase_flow: directFlow } }] });
          continue;
        }
        return cart;
      }
      throw new Error('Your cart changed during Gateway synchronization. Please retry.');
    } catch (error) {
      error.retrySync = true;
      error.message = `Gateway pairing could not be completed. ${error.message}`;
      throw error;
    }
  }
  async function add(items, attributes) {
    const prepared = prepareItems(items);
    return enqueue(async () => {
      await readCart();
      await request('cart/add.js', { items: prepared });
      try {
        if (attributes && Object.keys(attributes).length) await request('cart/update.js', { attributes });
        return await reconcile();
      } catch (error) {
        // Retry synchronization only: never add the collars a second time.
        error.cartChanged = true;
        error.retrySync = true;
        throw error;
      }
    });
  }

  const snapshotSignature = (cart) => {
    const quantities = {};
    cart.items.forEach((item) => { const key = identity(item); quantities[key] = (quantities[key] || 0) + item.quantity; });
    return JSON.stringify([sortedProperties(quantities), sortedProperties(Object.fromEntries(
      Object.entries(cart.attributes || {}).filter(([, value]) => value !== '' && value !== null))), cart.note || '']);
  };
  function buyNow(items) {
    return enqueue(async () => {
      const prepared = prepareItems(items);
      if (prepared.length !== 1 || !config.collarVariantIds.includes(Number(prepared[0].id)) ||
        prepared[0].properties?._commercial_model || sellingPlanId(prepared[0])) {
        throw new Error('Buy Now is only available for a direct MooCollar purchase.');
      }
      const snapshot = await readCart();
      const collar = prepared[0];
      const belongsToPurchase = (item) => !item.properties?._commercial_model && !sellingPlanId(item) &&
        ((isCollar(item) && identity(item) === identity(collar)) ||
          (isAutoGateway(item) && Number(item.variant_id) === Number(config.gatewayVariantId) && item.properties?._purchase_flow === directFlow));
      let clearAttempted = false;
      try {
        await gatewayVariant();
        clearAttempted = true;
        await request('cart/clear.js', {});
        if ((await readCart()).items.length) throw new Error('Your previous cart could not be cleared.');
        const attributes = Object.fromEntries(commercialAttributes.filter((key) => key in (snapshot.attributes || {})).map((key) => [key, '']));
        await request('cart/update.js', { attributes, note: snapshot.note || '' });
        await request('cart/add.js', { items: prepared });
        await reconcile();
        const cart = await readCart();
        const collarQuantity = cart.items.filter(isCollar).reduce((total, item) => total + item.quantity, 0);
        const gatewayQuantity = cart.items.filter(isGateway).reduce((total, item) => total + item.quantity, 0);
        if (!cart.items.every(belongsToPurchase) || collarQuantity !== Number(collar.quantity) || gatewayQuantity !== 1 ||
          commercialAttributes.some((key) => cart.attributes?.[key])) {
          throw new Error('Your Buy Now cart could not be verified. Please retry.');
        }
        return cart;
      } catch (error) {
        let restored = !clearAttempted;
        try {
          const current = await readCart();
          restored = snapshotSignature(current) === snapshotSignature(snapshot);
          if (!restored && clearAttempted && current.items.every(belongsToPurchase)) {
            // Never erase an unexpected item from another tab/client during recovery.
            await request('cart/clear.js', {});
            const original = snapshot.items.map((item) => ({ id: item.variant_id, quantity: item.quantity,
              properties: item.properties || {}, ...(sellingPlanId(item) ? { selling_plan: sellingPlanId(item) } : {}) }));
            if (original.length) await request('cart/add.js', { items: original });
            const attributes = Object.fromEntries(commercialAttributes.map((key) => [key, snapshot.attributes?.[key] || '']));
            await request('cart/update.js', { attributes, note: snapshot.note || '' });
            restored = snapshotSignature(await readCart()) === snapshotSignature(snapshot);
          }
        } catch { restored = false; }
        error.retrySync = false;
        error.cartChanged = false;
        error.retryBuyNow = restored;
        error.message += restored ? ' Your previous cart is unchanged or has been restored. Please try Buy Now again.'
          : ' Your previous cart could not be safely restored. Please review your cart before trying again.';
        throw error;
      }
    });
  }
  function mutate(path, data) {
    return enqueue(async () => {
      const cart = await readCart();
      const payload = { ...data };
      const resolve = (ref, value) => {
        const item = findLine(cart, ref);
        const quantity = typeof value === 'function' ? value(item) : Number(value);
        if (!Number.isSafeInteger(quantity) || quantity < 0 || (isCollar(item) && quantity !== 0 && !positiveQuantity(quantity))) {
          throw new Error('Please enter a whole number of collars (1 or more), or use Remove.');
        }
        return { key: item.key, quantity };
      };
      if (path === 'cart/change.js') {
        const resolved = resolve(payload.id, payload.quantity);
        payload.id = resolved.key;
        payload.quantity = resolved.quantity;
      }
      else if (path === 'cart/update.js' && payload.updates) {
        payload.updates = Object.fromEntries(Object.entries(payload.updates).map(([key, value]) => {
          const resolved = resolve(payload.lineReferences?.[key] || key, value);
          return [resolved.key, resolved.quantity];
        }));
      } else throw new Error('Unsupported cart operation.');
      delete payload.lineReferences;
      await request(path, payload);
      try { return await reconcile(); }
      catch (error) { error.cartChanged = true; throw error; }
    });
  }
  window.DairyMindDirectPurchase = { add, buyNow, reference, mutate, sync: () => enqueue(reconcile), readCart, positiveQuantity };
  document.dispatchEvent(new CustomEvent('dm:direct-purchase-ready'));
})();
